import { createAsyncThunk } from '@reduxjs/toolkit'

import { callErrorMsg } from 'helpers/errorMsg'
import { toastFailed, toastSuccess } from 'utils/toast'
import {
  getLocationDetail,
  getAvailableSlotInboundScheduleApi,
} from 'features/Enterprise/CapacityHandler/services/inboundSchedule'
import {
  deletePoNumber,
  getAvailableTimeSlot,
  getInboundScheduleDetail,
  getPoNumber,
  getSlotList,
  getVendors,
  PoNumberType,
  postInboundSchedule,
  putInboundSchedule,
  getAuditTrails,
  getReasons,
  postUploadFile,
  postPoDetailsEmail,
  type GetAvailableTimeSlotParamsType,
  type GetPoNumberParamsType,
  type PostInboundScheduleDataType,
  type PutInboundScheduleDataType,
  type GetAuditTrailsDataType,
  type GetSlotListType,
  type GetReasonType,
  AvailableTimeSlotType,
  getCommercialFeatureFlag,
} from 'features/Enterprise/CapacityHandler/services/inboundScheduleAdd'
import { getPurchaseOrderDetailApi } from 'features/PurchaseOrder/services/purchaseOrder'

import {
  SLICE_NAME,
  setLocation,
  setVendorList,
  setSlotList,
  setAvailableTimeSlot,
  setPoNumberList,
  setSelectedPoNumber,
  setSelectedPoNumberToDelete,
  setInboundScheduleDetail,
  setLoadingFetchDetail,
  setScheduleDate,
  setVendor,
  setAuditTrailsData,
  setReasons,
  setPoNumberDefaultData,
  setShippingTime,
  setTimeSlotList,
  setFfPoDetailsId,
} from './inboundScheduleAddSlice'

export const fetchGetLocationDetail = createAsyncThunk(
  `${SLICE_NAME}/fetchGetLocationDetail`,
  async (id: string, { dispatch }) => {
    try {
      const { data } = await getLocationDetail(id)
      dispatch(setLocation(data))
    } catch (error) {
      callErrorMsg(error)
    }
  },
)

export const fetchVendors = createAsyncThunk(
  `${SLICE_NAME}/fetchVendors`,
  async (companyName: string, { dispatch }) => {
    try {
      const {
        data: { data },
      } = await getVendors(companyName)

      dispatch(setVendorList(data))
    } catch (error) {
      callErrorMsg(error)
    }
  },
)

export const fetchSlotList = createAsyncThunk(
  `${SLICE_NAME}/fetchSlotList`,
  async (_, { dispatch }) => {
    try {
      const {
        data: { data },
      } = await getSlotList()
      dispatch(setSlotList(data as unknown as GetSlotListType['data']))
    } catch (error) {
      callErrorMsg(error)
    }
  },
)

export const fetchAvailableTimeSlot = createAsyncThunk(
  `${SLICE_NAME}/fetchAvailableTimeSlot`,
  async (params: GetAvailableTimeSlotParamsType, { dispatch, rejectWithValue }) => {
    try {
      const {
        data: { data },
      } = await getAvailableTimeSlot(params)

      dispatch(setAvailableTimeSlot(data))
      return data
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

export const fetchPoNumberList = createAsyncThunk(
  `${SLICE_NAME}/fetchPoNumberList`,
  async (params: GetPoNumberParamsType, { dispatch, rejectWithValue }) => {
    try {
      const {
        data: { data },
      } = await getPoNumber(params)

      dispatch(setPoNumberList(data))
      return data
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

export const saveInboundSchedule = createAsyncThunk(
  `${SLICE_NAME}/saveInboundSchedule`,
  async (data: PostInboundScheduleDataType, { rejectWithValue }) => {
    try {
      const res = await postInboundSchedule({ ...data })
      toastSuccess('Jadwal inbound PO berhasil dibuat.')
      return res.data
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

export const removePoNumber = createAsyncThunk(
  `${SLICE_NAME}/removePoNumber`,
  async (
    { scheduleId, selectedReason }: { scheduleId: string; selectedReason: string },
    { getState, rejectWithValue, dispatch },
  ) => {
    const {
      inboundScheduleAdd: { selectedPoNumberToDelete, selectedPoNumber, poNumberList },
      auth: {
        userData: { full_name, id },
      },
    } = getState() as StoreStateType

    try {
      const response = await deletePoNumber(scheduleId, selectedPoNumberToDelete?.id as number, {
        executed_by: full_name,
        user_id: id,
        reason: selectedReason,
      })
      toastSuccess(`PO ${selectedPoNumberToDelete?.purchase_order_number} berhasil dihapus.`)
      const filteredPoNumber = selectedPoNumber.filter(
        (po) => po.id !== selectedPoNumberToDelete?.id,
      )
      dispatch(setSelectedPoNumber(filteredPoNumber))
      dispatch(setPoNumberDefaultData(filteredPoNumber.map((po) => po.purchase_order_id)))

      const updatedPoNumberList = [
        ...poNumberList.map((po) => ({
          id: po.purchase_order_id,
          purchaseOrderId: po.purchase_order_id,
          purchaseOrderNumber: po.purchase_order_number,
          totalPcs: po.total_pcs,
          vendorName: po.vendor_name,
          isVip: po.is_vip,
          reason: '',
        })),
        {
          id: selectedPoNumberToDelete?.purchase_order_id,
          purchaseOrderId: selectedPoNumberToDelete?.purchase_order_id,
          purchaseOrderNumber: selectedPoNumberToDelete?.purchase_order_number,
          totalPcs: selectedPoNumberToDelete?.total_pcs,
          vendorName: selectedPoNumberToDelete?.vendor_name,
          isVip: selectedPoNumberToDelete?.is_vip,
          reason: '',
        },
      ] as PoNumberType[]
      dispatch(setPoNumberList(updatedPoNumberList))
      dispatch(setSelectedPoNumberToDelete(null))

      return response.data
    } catch (error) {
      toastFailed(`PO ${selectedPoNumberToDelete?.purchase_order_number} gagal dihapus.`)
      return rejectWithValue(error)
    }
  },
)

export const updateInboundSchedule = createAsyncThunk(
  `${SLICE_NAME}/updateInboundSchedule`,
  async (params: PutInboundScheduleDataType & { scheduleId: string }, { rejectWithValue }) => {
    const { scheduleId, ...updateData } = params
    try {
      const res = await putInboundSchedule(scheduleId, updateData)
      toastSuccess('Jadwal inbound PO berhasil diubah.')

      return res.data
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

export const fetchInboundScheduleDetail = createAsyncThunk(
  `${SLICE_NAME}/fetchInboundScheduleDetail`,
  async (scheduleId: string, { dispatch, getState }) => {
    dispatch(setLoadingFetchDetail(true))

    const {
      inboundScheduleAdd: { pageType },
    } = getState() as StoreStateType

    try {
      const {
        data: { data },
      } = await getInboundScheduleDetail(scheduleId)

      dispatch(setInboundScheduleDetail(data))
      dispatch(setScheduleDate(new Date(data.date)))
      dispatch(setSelectedPoNumber(data.purchase_orders.map((po) => ({ ...po, hasStored: true }))))
      dispatch(setPoNumberDefaultData(data.purchase_orders.map((po) => po.purchase_order_id)))
      dispatch(setShippingTime(data?.register_start_at || '00:00'))

      if (data.vendor) {
        dispatch(setVendor({ id: data.vendor.vendor_id, companyName: data.vendor.vendor_name }))
      }

      if (pageType === 'UPDATE' || pageType === 'VIEW') {
        dispatch(
          fetchAvailableTimeSlot({
            date: data.date,
            slot: data.time_slot_ids.length || 1,
            loading_dock_id: data.loading_dock_id,
          }),
        )

        const { data: availableSlotData } = await getAvailableSlotInboundScheduleApi(
          data.loading_dock_id,
        )
        dispatch(setTimeSlotList(availableSlotData.data as never as AvailableTimeSlotType[]))

        dispatch(
          setLocation({
            location_id: availableSlotData?.data?.[0]?.location_id || 0,
            location_name: '',
          }),
        )

        const poNumberList = await dispatch(
          fetchPoNumberList({
            destinationId: availableSlotData?.data?.[0]?.location_id || 0,
          }),
        ).unwrap()
        const filteredPoNumberList = poNumberList.filter(
          (item) => !data.purchase_orders.find((po) => item.id === po.purchase_order_id),
        )
        dispatch(setPoNumberList(filteredPoNumberList))
      }
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setLoadingFetchDetail(false))
    }
  },
)

export const fetchAuditTrail = createAsyncThunk(
  `${SLICE_NAME}/fetchAuditTrail`,
  async ({ scheduleId, cursor }: { scheduleId: string; cursor?: string }, { dispatch }) => {
    try {
      dispatch(setLoadingFetchDetail(true))
      const {
        data: { data, pagination },
      } = await getAuditTrails(scheduleId, cursor || '')
      dispatch(setAuditTrailsData({ data, pagination } as unknown as GetAuditTrailsDataType))
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setLoadingFetchDetail(false))
    }
  },
)

export const fetchReasons = createAsyncThunk(
  `${SLICE_NAME}/fetchReasons`,
  async (type: GetReasonType, { dispatch }) => {
    try {
      const {
        data: { data },
      } = await getReasons(type)
      dispatch(setReasons(data))
    } catch (error) {
      callErrorMsg(error)
    }
  },
)

export const fetchPODetails = createAsyncThunk(
  `${SLICE_NAME}/fetchPODetails`,
  async (poId: number) => {
    try {
      const {
        data: { data, inbound_schedule },
      } = await getPurchaseOrderDetailApi(poId)
      return { data, inbound_schedule }
    } catch (error) {
      callErrorMsg(error)
      return error
    }
  },
)

export const uploadPdf = createAsyncThunk(`${SLICE_NAME}/uploadPdf`, async (payload: FormData) => {
  try {
    const { data } = await postUploadFile(payload)
    return data.data.fileUrl
  } catch (error) {
    callErrorMsg(error)
    return undefined
  }
})

export const sendPoDetailsEmail = createAsyncThunk(
  `${SLICE_NAME}/uploadPdf`,
  async ({
    schedule_id,
    payload,
  }: {
    schedule_id: string
    payload: { purchase_order_id: number; pdf_url: string }
  }) => {
    try {
      const { data } = await postPoDetailsEmail(schedule_id, payload)
      return data?.message || ''
    } catch (error) {
      callErrorMsg(error)
      return ''
    }
  },
)

export const fetchFeatureFlagPoDetails = createAsyncThunk(
  `${SLICE_NAME}/uploadPdf`,
  async (_, { dispatch }) => {
    try {
      const { data } = await getCommercialFeatureFlag()
      dispatch(setFfPoDetailsId(Number(data?.data?.FF_PO_FULLY_MIGRATED_MIN_ID)))
    } catch (error) {
      callErrorMsg(error)
    }
  },
)
